import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";

import dayjs from "dayjs";
import { useAtom, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import {
  Box,
  Tabs,
  Text,
  Badge,
  Group,
  Stack,
  Table,
  Button,
  Anchor,
  Center,
  Divider,
  Popover,
  Checkbox,
  TextInput,
  ActionIcon,
  Pagination,
  createStyles,
  UnstyledButton,
} from "@mantine/core";
import {
  BsSortAlphaUp,
  BsSortAlphaDown,
  BsChevronExpand,
} from "react-icons/bs";
import sortBy from "lodash/sortBy";
import { AiOutlineEye } from "react-icons/ai";
import { BiFilter, BiSearch } from "react-icons/bi";
import { useCollection } from "react-firebase-hooks/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebouncedState, usePagination } from "@mantine/hooks";
import { getCountFromServer, query, setDoc } from "firebase/firestore";

import { Header } from "@components/Header/Header";
import { Content } from "@components/Content/Content";
import { PageLoader } from "@components/PageLoader/PageLoader";

import { Exporter } from "@utils/Exporter";
import { SubmissionModal } from "./components/SubmissionModal";
import { selectedUserIdAtom } from "./atoms/selectedUserId.atom";
import {
  getFormCollectionRef,
  getFormDocRef,
} from "../../services/form.services";
import { selectedFormNameAtom } from "./atoms/selectedFormName.atom";
import { AuthContext } from "@components/AuthProvider/contexts/Auth.context";

import { ISubmission } from "@models/Submission.model";

import FORM_DETAILS from "../../jsons/FORM_DETAILS.json";
import { DeleteModal, IDeleteModalRef } from "./components/DeleteModal";
import { calculateFormProgress } from "@components/FormProvider/FormProvider";
import { useForm } from "@mantine/form";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },

  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.xs}px`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[1],
    },
    "&:active": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[5]
          : theme.colors.gray[2],
    },
  },

  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
}));

const Th: FC<{
  children: string;
  reversed?: boolean;
  sorted?: boolean;
  onSort?: () => void;
}> = ({ children, reversed, sorted, onSort }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? BsSortAlphaUp
      : BsSortAlphaDown
    : BsChevronExpand;
  return (
    <th className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group position="apart">
          <Text weight={500} size="sm">
            {t(children)}
          </Text>
          <Center className={classes.icon}>
            <Icon size={14} />
          </Center>
        </Group>
      </UnstyledButton>
    </th>
  );
};

enum EFilter {
  DELETED = "DELETED",
  COMPLETED = "COMPLETED",
  INCOMPLETE = "INCOMPLETE",
}

export const SubmissionsRoute: FC = () => {
  const [searchParams] = useSearchParams();

  const deleteModalRef = useRef<IDeleteModalRef | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const filterForm = useForm<{ filter: EFilter[] }>({
    initialValues: { filter: [EFilter.COMPLETED] },
  });

  const [sort, setSort] = useState<
    "firstName" | "lastName" | "email" | "progress" | "updatedAt" | null
  >(null);
  const [reversed, setReversed] = useState(false);

  const [q, setQ] = useDebouncedState("", 500);

  const {
    user: { isAdmin },
  } = useContext(AuthContext)!;

  const navigate = useNavigate();

  if (!isAdmin) navigate("/portal");

  const [submissionsCount, setSubmissionsCount] = useState<{
    volunteering: number;
    eventRegistration: number;
    demoRegistration: number;
  }>({ demoRegistration: 0, eventRegistration: 0, volunteering: 0 });

  const [isLoading, setIsLoading] = useState(true);
  const setSelectedUserId = useSetAtom(selectedUserIdAtom);
  const [selected, setSelected] = useAtom(selectedFormNameAtom);

  const [collection, isCollectionLoading] = useCollection(
    getFormCollectionRef(selected),
  );

  const { t } = useTranslation();

  const submissions = useMemo(() => {
    const showIncomplete = filterForm.values.filter.includes(
      EFilter.INCOMPLETE,
    );
    const showDeleted = filterForm.values.filter.includes(EFilter.DELETED);
    const showCompleted = filterForm.values.filter.includes(EFilter.COMPLETED);

    const filters: EFilter[] = [];
    if (showDeleted) filters.push(EFilter.DELETED);
    if (showCompleted) filters.push(EFilter.COMPLETED);
    if (showIncomplete) filters.push(EFilter.INCOMPLETE);

    let result = (collection?.docs || []).reduce((pv, doc) => {
      const submission = {
        ...doc.data(),
        uid: doc.id,
      } as ISubmission<{}> & {
        uid: string;
      };

      Object.assign(submission, {
        progress: calculateFormProgress(submission.form, selected),
      });

      const {
        user: { email, firstName, lastName },
        progress,
        isDeleted,
      } = submission;

      const isCompleted = ~~progress === 100;
      const isIncomplete = !isCompleted;

      if (isDeleted && !showDeleted) return pv;

      const states: EFilter[] = [];
      if (isDeleted) states.push(EFilter.DELETED);
      if (isCompleted) states.push(EFilter.COMPLETED);
      if (isIncomplete) states.push(EFilter.INCOMPLETE);

      if (!filters.some((item) => states.includes(item))) return pv;

      if (
        email
          .trim()
          .toLocaleLowerCase()
          .includes(q.toLocaleLowerCase().trim()) ||
        firstName
          .trim()
          .toLocaleLowerCase()
          .includes(q.toLocaleLowerCase().trim()) ||
        lastName
          .trim()
          .toLocaleLowerCase()
          .includes(q.toLocaleLowerCase().trim())
      )
        return [...pv, submission];

      return pv;
    }, [] as (ISubmission<{}> & { uid: string })[]);
    if (sort) {
      result = sortBy(result, (item) => {
        const {
          progress,
          updatedAt,
          user: { email, firstName, lastName },
        } = item;
        switch (sort) {
          case "email":
            return email.toLocaleLowerCase();
          case "firstName":
            return firstName.toLocaleLowerCase();
          case "lastName":
            return lastName.toLocaleLowerCase();
          case "progress":
            return progress;
          case "updatedAt":
            return -(updatedAt || -Infinity);
        }
      });

      if (reversed) result.reverse();
    }

    return result;
  }, [collection?.docs, filterForm.values.filter, q, reversed, selected, sort]);

  const itemsPerPage = 20;
  const totalItems = submissions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const { active, setPage } = usePagination({ total: totalPages });

  const setPageRef = useRef(setPage);
  useEffect(() => {
    setPageRef.current = setPage;
  });

  const currentPageIndex = active - 1;
  const startingIdx = currentPageIndex * itemsPerPage;
  const endingIdx = startingIdx + itemsPerPage;
  const isInPage = (idx: number) => idx >= startingIdx && idx < endingIdx;

  useEffect(() => {
    setPageRef.current(0);
  }, [q, selected, filterForm.values.filter]);

  const handleClickFactory = (column: typeof sort) => () => {
    const pvColumn = sort;
    if (pvColumn === column) {
      if (reversed === true) {
        setReversed(false);
        setSort(null);
      } else {
        setReversed((pv) => !pv);
      }
    } else {
      setReversed(false);
      setSort(column);
    }
  };

  const handleExport = () => {
    switch (selected) {
      case "demo-registration":
        Exporter.download(
          Exporter.exportDemoRegistrationSubmissions(submissions),
          t(FORM_DETAILS.DemoRegistration.title),
        );
        break;
      case "event-registration":
        Exporter.download(
          Exporter.exportEventRegistrationSubmissions(submissions),
          t(FORM_DETAILS.EventRegistration.title),
        );
        break;
      case "volunteering":
        Exporter.download(
          Exporter.exportVolunteeringSubmissions(submissions),
          t(FORM_DETAILS.Volunteering.title),
        );
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    setIsLoading(true);
    const volunteeringCollectionRef = getFormCollectionRef("volunteering");
    const eventRegistrationCollectionRef =
      getFormCollectionRef("event-registration");
    const demoRegistrationCollectionRef =
      getFormCollectionRef("demo-registration");

    const volunteeringSubmissions = getCountFromServer(
      query(volunteeringCollectionRef),
    );
    const eventRegistrationSubmissions = getCountFromServer(
      eventRegistrationCollectionRef,
    );
    const demoRegistrationSubmissions = getCountFromServer(
      demoRegistrationCollectionRef,
    );
    Promise.all([
      volunteeringSubmissions,
      eventRegistrationSubmissions,
      demoRegistrationSubmissions,
    ])
      .then((e) => {
        setSubmissionsCount({
          volunteering: e[0].data().count,
          eventRegistration: e[1].data().count,
          demoRegistration: e[2].data().count,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const defaultTab = searchParams.get("tab") as
      | "volunteering"
      | "demo-registration"
      | "event-registration"
      | null;
    if (
      !defaultTab ||
      !["volunteering", "demo-registration", "event-registration"].includes(
        defaultTab,
      )
    )
      return;

    const defaultUid = searchParams.get("uid");
    setSelected(defaultTab);
    defaultUid && setSelectedUserId(defaultUid);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <DeleteModal ref={deleteModalRef} isDeleting={isDeleting} />
      <Header />
      <Content>
        {isLoading ? (
          <PageLoader />
        ) : (
          <Stack sx={{ flex: 1 }}>
            <Group position="apart">
              <Tabs
                value={selected}
                onTabChange={(e) =>
                  setSelected(
                    (e?.valueOf() || "volunteering") as typeof selected,
                  )
                }
                variant="pills"
              >
                <Tabs.List>
                  <Tabs.Tab
                    disabled={!submissionsCount.eventRegistration}
                    value={"event-registration" as typeof selected}
                    rightSection={
                      <Badge variant="filled" size="xs">
                        {submissionsCount.eventRegistration}
                      </Badge>
                    }
                  >
                    {t("Event Registration")}
                  </Tabs.Tab>
                </Tabs.List>
              </Tabs>

              <Group>
                <TextInput
                  w={300}
                  size="xs"
                  icon={<BiSearch />}
                  onChange={(e) => {
                    setQ(e.target.value.toLocaleLowerCase());
                  }}
                  placeholder={`${t("Search")}...`}
                />
                <Popover position="bottom-end" withArrow>
                  <Popover.Target>
                    <ActionIcon color="purple" variant="outline">
                      <BiFilter />
                    </ActionIcon>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Checkbox.Group
                      label={t("submissions.filter.label")}
                      size="xs"
                      orientation="vertical"
                      spacing="xs"
                      {...filterForm.getInputProps("filter")}
                    >
                      <Checkbox
                        label={t("submissions.filter.options.completed")}
                        value={EFilter.COMPLETED}
                      />
                      <Checkbox
                        label={t("submissions.filter.options.incomplete")}
                        value={EFilter.INCOMPLETE}
                      />
                      <Checkbox
                        label={t("submissions.filter.options.deleted")}
                        value={EFilter.DELETED}
                      />
                    </Checkbox.Group>
                  </Popover.Dropdown>
                </Popover>
                <Divider orientation="vertical" />
                <Button size="xs" variant="outline" onClick={handleExport}>
                  {t("labels.export")}
                </Button>
              </Group>
            </Group>
            {isCollectionLoading ? (
              <PageLoader />
            ) : (
              <>
                {!collection?.docs.length ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      textAlign: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Text weight="bold">No Submission</Text>
                    <Text color="dimmed" size="xs">
                      No one has made a submission.
                    </Text>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, position: "relative" }}>
                    <Table striped highlightOnHover>
                      <thead>
                        <tr>
                          <Th
                            onSort={handleClickFactory("firstName")}
                            sorted={sort === "firstName"}
                            reversed={reversed}
                          >
                            First Name
                          </Th>
                          <Th
                            onSort={handleClickFactory("lastName")}
                            sorted={sort === "lastName"}
                            reversed={reversed}
                          >
                            Last Name
                          </Th>
                          <Th
                            onSort={handleClickFactory("email")}
                            sorted={sort === "email"}
                            reversed={reversed}
                          >
                            Email Address
                          </Th>
                          <Th
                            onSort={handleClickFactory("progress")}
                            sorted={sort === "progress"}
                            reversed={reversed}
                          >
                            Completed
                          </Th>
                          <Th
                            onSort={handleClickFactory("updatedAt")}
                            sorted={sort === "updatedAt"}
                            reversed={reversed}
                          >
                            Updated
                          </Th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions
                          .filter((_, idx) => isInPage(idx))
                          .map(
                            ({
                              uid,
                              progress,
                              updatedAt,
                              isDeleted,
                              isSubmitted,
                              user: { email, firstName, lastName },
                            }) => (
                              <tr key={uid}>
                                <td>{firstName}</td>
                                <td>{lastName}</td>
                                <td>
                                  <Anchor href={`mailto:${email}`}>
                                    {email}
                                  </Anchor>
                                </td>
                                <td>
                                  <Badge
                                    variant="outline"
                                    color={
                                      parseInt(progress.toString()) === 100
                                        ? "green"
                                        : "purple"
                                    }
                                  >
                                    {parseInt(progress.toString()) || "0"}%
                                  </Badge>
                                </td>
                                <td>
                                  <Text color="dimmed" size="sm">
                                    {updatedAt
                                      ? dayjs(updatedAt).fromNow()
                                      : ""}
                                  </Text>
                                </td>
                                <td>
                                  <Group>
                                    <Button
                                      size="xs"
                                      color="green"
                                      variant="light"
                                      leftIcon={<AiOutlineEye />}
                                      onClick={() => setSelectedUserId(uid)}
                                    >
                                      {t("View")}
                                    </Button>
                                    {!isDeleted && (
                                      <Button
                                        size="xs"
                                        color="red"
                                        variant="light"
                                        leftIcon={<AiOutlineEye />}
                                        onClick={() => {
                                          deleteModalRef.current
                                            ?.confirm()
                                            .then(async (confirmed) => {
                                              if (confirmed) {
                                                setIsDeleting(true);
                                                await setDoc(
                                                  getFormDocRef({
                                                    uid,
                                                    formName: selected,
                                                  }),
                                                  { isDeleted: true },
                                                  { merge: true },
                                                );
                                              }
                                              setIsDeleting(false);
                                              deleteModalRef.current?.close();
                                            });
                                        }}
                                      >
                                        {t("Delete")}
                                      </Button>
                                    )}
                                  </Group>
                                </td>
                              </tr>
                            ),
                          )}
                      </tbody>
                    </Table>
                    {totalPages > 1 && (
                      <Pagination
                        mt={20}
                        position="center"
                        total={totalPages}
                        page={active}
                        onChange={setPage}
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}
      </Content>
      <SubmissionModal />
    </>
  );
};
